import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import Button from "@material-ui/core/Button";
import Avatar from "@material-ui/core/Avatar";
import Typography from "@material-ui/core/Typography";
import TextField from "@material-ui/core/TextField";
import CircularProgress from "@material-ui/core/CircularProgress";
import Autocomplete from "@material-ui/lab/Autocomplete";
import { Alert } from "@material-ui/lab";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";
import { mergeCandidates, mergeBlockReason } from "./mergeCandidates";

const t = key => i18n.t(`mergeContactModal.${key}`);

const ContactLine = ({ label, contact }) => (
  <div style={{ display: "flex", alignItems: "center", gap: 12, margin: "8px 0" }}>
    <Avatar src={contact?.urlPicture || undefined}>{contact?.name?.[0]}</Avatar>
    <div>
      <Typography variant="caption" color="textSecondary">{label}</Typography>
      <Typography variant="body1">{contact?.name}</Typography>
      <Typography variant="body2" color="textSecondary">{contact?.number}</Typography>
    </div>
  </div>
);

/**
 * #4408 — mescla outro contato NO `target` (o da linha clicada). O backend
 * enfileira e responde na hora; o resultado chega pelo socket (página Contatos).
 */
const MergeContactModal = ({ open, target, onClose }) => {
  const [search, setSearch] = useState("");
  const [options, setOptions] = useState([]);
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [source, setSource] = useState(null);
  const [preview, setPreview] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) {
      setSearch("");
      setOptions([]);
      setSource(null);
      setPreview(null);
    }
  }, [open]);

  useEffect(() => {
    if (!open || search.length < 2) {
      setLoadingOptions(false);
      return undefined;
    }
    // `alive`: resposta atrasada de uma busca anterior não sobrescreve a atual.
    let alive = true;
    const timer = setTimeout(async () => {
      setLoadingOptions(true);
      try {
        const { data } = await api.get("/contacts/list", { params: { name: search } });
        if (alive) setOptions(mergeCandidates(data, target));
      } catch (err) {
        if (alive) toastError(err);
      }
      if (alive) setLoadingOptions(false);
    }, 400);
    return () => {
      alive = false;
      clearTimeout(timer);
    };
  }, [open, search, target]);

  useEffect(() => {
    // Trocou o contato: a prévia anterior some na hora. Senão o aviso de B1
    // ficava na tela com o Confirmar ligado, e o Confirmar mandava B2.
    setPreview(null);
    if (!source || !target) return undefined;
    let alive = true;
    api
      .get(`/contacts/${target.id}/merge-preview`, { params: { sourceId: source.id } })
      .then(({ data }) => alive && setPreview(data))
      .catch(err => {
        toastError(err);
        if (alive) setSource(null);
      });
    return () => {
      alive = false;
    };
  }, [source, target]);

  // A prévia só vale para o contato escolhido agora.
  const previewMatches = Boolean(source && preview?.source?.id === source.id);

  const handleConfirm = async () => {
    if (!previewMatches) return;
    setSubmitting(true);
    try {
      await api.post(`/contacts/${target.id}/merge`, { sourceId: source.id });
      toast.info(i18n.t("contacts.toasts.mergeQueued"));
      onClose();
    } catch (err) {
      toastError(err);
    }
    setSubmitting(false);
  };

  const counts = preview?.counts || {};
  const moving = Object.keys(counts).filter(k => k !== "openTickets" && counts[k] > 0);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t("title")}</DialogTitle>
      <DialogContent dividers>
        <ContactLine label={t("keeps")} contact={target} />

        <Autocomplete
          options={options}
          loading={loadingOptions}
          value={source}
          filterOptions={x => x}
          getOptionLabel={o => (o ? `${o.name} — ${o.number}` : "")}
          getOptionSelected={(o, v) => o.id === v.id}
          // tipo diferente fica visível, mas desabilitado e com o motivo
          getOptionDisabled={o => Boolean(mergeBlockReason(o, target))}
          renderOption={o => {
            const reason = mergeBlockReason(o, target);
            return (
              <div>
                <Typography variant="body2">{`${o.name} — ${o.number}`}</Typography>
                {reason && (
                  <Typography variant="caption" color="textSecondary">
                    {t(`blocked.${reason}`)}
                  </Typography>
                )}
              </div>
            );
          }}
          noOptionsText={t("noCandidates")}
          onChange={(_e, value) => {
            // o MUI v4 só bloqueia a opção desabilitada por CSS (pointer-events);
            // a guarda aqui garante que um contato bloqueado nunca vira escolha
            if (value && mergeBlockReason(value, target)) return;
            setSource(value);
          }}
          onInputChange={(_e, value, reason) => {
            // "reset" = o Autocomplete escrevendo o rótulo da opção escolhida;
            // não é busca do usuário.
            if (reason === "reset") return;
            setSearch(value);
          }}
          renderInput={params => (
            <TextField
              {...params}
              label={t("absorbed")}
              placeholder={t("searchPlaceholder")}
              variant="outlined"
              margin="dense"
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loadingOptions ? <CircularProgress size={18} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                )
              }}
            />
          )}
        />

        {source && !previewMatches && <CircularProgress size={24} style={{ marginTop: 12 }} />}

        {previewMatches && (
          <>
            <ContactLine label={t("absorbed")} contact={preview.source} />
            {moving.length > 0 && (
              <>
                <Typography variant="body2">{t("willMove")}</Typography>
                <ul style={{ marginTop: 4 }}>
                  {moving.map(k => (
                    <li key={k}>
                      <Typography variant="body2">
                        {counts[k]} {t(`counts.${k}`)}
                      </Typography>
                    </li>
                  ))}
                </ul>
              </>
            )}
            {counts.openTickets > 0 && (
              <Alert severity="warning" style={{ marginBottom: 8 }}>
                {i18n.t("mergeContactModal.warningOpenTickets", {
                  name: preview.source.name,
                  count: counts.openTickets
                })}
              </Alert>
            )}
            <Alert severity="error">
              {i18n.t("mergeContactModal.warningDelete", { name: preview.source.name })}
            </Alert>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>{t("cancel")}</Button>
        <Button
          color="secondary"
          variant="contained"
          disabled={!previewMatches || submitting}
          onClick={handleConfirm}
        >
          {t("confirm")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MergeContactModal;
